import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet, apiPut } from "../../shared/apiClient";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../../shared/ToastContext";
import { PdfUploadField } from "../../shared/PdfUploadField";

const inputClass =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20";

const emptyProfile = {
  name: "",
  phoneNo: "",
  mobileNo: "",
  whatsappNo: "",
  email1: "",
  email2: "",
  address: "",
  fBlink: "",
  instalink: "",
  youtubelink: "",
  googlelink: "",
  twitterlink: "",
  snapchatlink: "",
  whatsApplink: "",
  smSusername: "",
  smSpassword: "",
  smSId: "",
  gmailId: "",
  gmailPassword: "",
  emquiryReceiceMail: "",
  enquiryReceiveSubject: "",
  profilePDF: ""
};

const sections = [
  {
    title: "Company",
    fields: [
      ["name", "Name"],
      ["phoneNo", "Phone No"],
      ["mobileNo", "Mobile No"],
      ["whatsappNo", "Whatsapp No"],
      ["email1", "Email 1"],
      ["email2", "Email 2"],
      ["address", "Address"]
    ]
  },
  {
    title: "Social Links",
    fields: [
      ["fBlink", "Facebook Link"],
      ["instalink", "Instagram Link"],
      ["youtubelink", "Youtube Link"],
      ["googlelink", "Google Link"],
      ["twitterlink", "Twitter Link"],
      ["snapchatlink", "Snapchat Link"],
      ["whatsApplink", "WhatsApp Link"]
    ]
  },
  {
    title: "SMS",
    fields: [
      ["smSusername", "SMS Username"],
      ["smSpassword", "SMS Password"],
      ["smSId", "SMS ID"]
    ]
  },
  {
    title: "Enquiry Email",
    fields: [
      ["gmailId", "Gmail ID"],
      ["gmailPassword", "Gmail Password"],
      ["emquiryReceiceMail", "Enquiry Receive Mail"],
      ["enquiryReceiveSubject", "Enquiry Receive Subject"]
    ]
  }
];

export function ProfilePage() {
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [profile, setProfile] = useState(emptyProfile);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const effectiveCustomerId = useMemo(
    () => (isAdmin ? selectedCustomerId : user?.customerId || ""),
    [isAdmin, selectedCustomerId, user]
  );

  useEffect(() => {
    const loadCustomers = async () => {
      if (!isAdmin) return;
      const res = await apiGet("/api/Customers");
      if (res.ok) {
        const rows = Array.isArray(res.data) ? res.data : [];
        setCustomers(rows);
        if (!selectedCustomerId && rows[0]?.id) setSelectedCustomerId(String(rows[0].id));
      }
    };
    void loadCustomers();
  }, [isAdmin, selectedCustomerId]);

  const loadProfile = useCallback(async () => {
    if (!effectiveCustomerId) return;
    setLoading(true);
    const q = isAdmin ? `?customerId=${encodeURIComponent(effectiveCustomerId)}` : "";
    const res = await apiGet(`/api/Profiles${q}`);
    setLoading(false);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to load profile"), "error");
      return;
    }
    setProfile({ ...emptyProfile, ...(res.data || {}) });
  }, [effectiveCustomerId, isAdmin, toast]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  const save = async (event) => {
    event.preventDefault();
    if (!effectiveCustomerId) {
      toast.push("Select customer first", "error");
      return;
    }
    setSaving(true);
    const res = await apiPut("/api/Profiles", {
      ...profile,
      customerId: Number(effectiveCustomerId)
    });
    setSaving(false);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to save profile"), "error");
      return;
    }
    toast.push("Profile saved", "success");
    await loadProfile();
  };

  const testEmail = async () => {
    if (!effectiveCustomerId) return;
    setTesting(true);
    const q = isAdmin ? `?customerId=${encodeURIComponent(effectiveCustomerId)}` : "";
    const res = await apiGet(`/api/Enquiries/testEmail${q}`);
    setTesting(false);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to send test email"), "error");
      return;
    }
    toast.push("Test email sent", "success");
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Profile</h1>
            <p className="mt-1 text-sm text-slate-500">Company contact, social, SMS, and enquiry email settings.</p>
          </div>
          {isAdmin && (
            <select className={inputClass} value={selectedCustomerId} onChange={(e) => setSelectedCustomerId(e.target.value)}>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name || c.code || `Customer ${c.id}`}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      <form onSubmit={save} className="space-y-4">
        {sections.map((section) => (
          <div key={section.title} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">{section.title}</h2>
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
              {section.fields.map(([key, label]) => (
                <div key={key} className={key === "address" ? "md:col-span-2" : ""}>
                  <label className="mb-1.5 block text-xs font-medium text-slate-600">{label}</label>
                  {key === "address" ? (
                    <textarea className={inputClass} rows={3} value={profile[key] || ""} onChange={(e) => setProfile((prev) => ({ ...prev, [key]: e.target.value }))} />
                  ) : (
                    <input className={inputClass} type={key.toLowerCase().includes("password") ? "password" : "text"} value={profile[key] || ""} onChange={(e) => setProfile((prev) => ({ ...prev, [key]: e.target.value }))} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Profile PDF</h2>
          <div className="mt-4">
            <PdfUploadField
              label="Upload PDF"
              labelClassName="mb-1.5 block text-xs font-medium text-slate-600"
              value={profile.profilePDF || ""}
              companyName={profile.name || ""}
              replacePath={profile.profilePDF || ""}
              onChange={(path) => setProfile((prev) => ({ ...prev, profilePDF: path }))}
            />
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" onClick={() => void testEmail()} disabled={testing || loading} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60">
            {testing ? "Sending..." : "Test Email"}
          </button>
          <button type="submit" disabled={saving || loading} className="rounded-lg bg-brand-800 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60">
            {saving ? "Saving..." : "Save Profile"}
          </button>
        </div>
      </form>
    </div>
  );
}
